import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Returns the display name for the current user, derived from the ID token's
 * `given_name` and `family_name` claims. Falls back to the provided username
 * (e.g., the Cognito ULID) while loading or if the token lacks name claims.
 *
 * @param fallbackUsername - The raw username to show until the name is resolved.
 * @returns The user's display name, or `fallbackUsername` if unavailable.
 */
export function useDisplayName(fallbackUsername: string): string {
    const [displayName, setDisplayName] = useState(fallbackUsername);

    useEffect(() => {
        let cancelled = false;

        fetchAuthSession()
            .then((session) => {
                if (cancelled) return;
                const payload = session.tokens?.idToken?.payload;
                const givenName = payload?.given_name as string | undefined;
                const familyName = payload?.family_name as string | undefined;
                const parts = [givenName, familyName].filter(Boolean);
                const name = parts.join(' ');
                if (name) {
                    setDisplayName(name);
                }
            })
            .catch(() => {
                // Keep fallback — no PII in logs
            });

        return () => {
            cancelled = true;
        };
    }, [fallbackUsername]);

    return displayName;
}
